import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Tag, Spin, Empty, Button, Space, Avatar, Badge, Input, Tooltip } from 'antd';
import { 
  TeamOutlined, 
  UserOutlined,
  CrownOutlined,
  StarOutlined,
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
  CopyOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Text } = Typography;

interface Socio {
  nome: string;
  qualificacao: string;
  data_entrada: string;
  faixa_etaria: string;
  cnpj_cpf: string;
}

interface EmailInferido {
  nome: string;
  email_inferido: string;
  confianca: string;
}

interface BuyingCommittee {
  decisores: Socio[];
  influenciadores: Socio[];
  outros: Socio[];
}

interface BuyingCommitteeCardProps {
  cnpj: string;
  autoLoad?: boolean;
}

export const BuyingCommitteeCard: React.FC<BuyingCommitteeCardProps> = ({ 
  cnpj, 
  autoLoad = true 
}) => {
  const [committee, setCommittee] = useState<BuyingCommittee | null>(null);
  const [emails, setEmails] = useState<EmailInferido[]>([]);
  const [contagem, setContagem] = useState({ decisores: 0, influenciadores: 0, outros: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchCommittee = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_SERVIDOR2.BASE}/api/v1/ia/buying-committee/${cnpj}`
      );
      
      setCommittee(response.data.committee);
      setContagem(response.data.contagem || { decisores: 0, influenciadores: 0, outros: 0 });
      setEmails(response.data.emails_inferidos || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao buscar sócios');
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (autoLoad && cnpj && !hasLoaded) {
      fetchCommittee();
    }
  }, [autoLoad, cnpj, hasLoaded]);

  useEffect(() => {
    setHasLoaded(false);
    setCommittee(null);
    setError(null);
  }, [cnpj]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getRoleIcon = (tipo: 'decisor' | 'influenciador' | 'outro') => {
    switch (tipo) {
      case 'decisor':
        return <CrownOutlined style={{ color: '#faad14' }} />;
      case 'influenciador':
        return <StarOutlined style={{ color: '#1890ff' }} />;
      default:
        return <UserOutlined style={{ color: '#999' }} />;
    }
  };

  const getRoleColor = (tipo: 'decisor' | 'influenciador' | 'outro') => {
    switch (tipo) {
      case 'decisor':
        return 'gold';
      case 'influenciador':
        return 'blue';
      default:
        return 'default';
    }
  };

  const filterSocios = (socios: Socio[]) => {
    if (!searchText) return socios;
    const search = searchText.toLowerCase();
    return socios.filter(s => 
      s.nome.toLowerCase().includes(search) ||
      s.qualificacao.toLowerCase().includes(search)
    );
  };

  const renderSocioItem = (socio: Socio, tipo: 'decisor' | 'influenciador' | 'outro') => {
    const inicial = socio.nome?.charAt(0)?.toUpperCase() || '?';
    
    return (
      <List.Item style={{ padding: '8px 0' }}>
        <List.Item.Meta
          avatar={
            <Badge count={getRoleIcon(tipo)} offset={[-5, 25]}>
              <Avatar 
                style={{ 
                  backgroundColor: tipo === 'decisor' ? '#faad14' : 
                                   tipo === 'influenciador' ? '#1890ff' : '#999'
                }}
              >
                {inicial}
              </Avatar>
            </Badge>
          }
          title={
            <Space>
              <Text strong>{socio.nome}</Text>
              <Tag color={getRoleColor(tipo)} style={{ fontSize: 10 }}>
                {tipo === 'decisor' ? 'Decisor' : tipo === 'influenciador' ? 'Influenciador' : 'Outro'}
              </Tag>
            </Space>
          }
          description={
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {socio.qualificacao}
              </Text>
              {socio.data_entrada && socio.data_entrada !== 'N/A' && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Entrada: {socio.data_entrada}
                </Text>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  const allSocios = committee ? [
    ...committee.decisores.map(s => ({ ...s, tipo: 'decisor' as const })),
    ...committee.influenciadores.map(s => ({ ...s, tipo: 'influenciador' as const })),
    ...committee.outros.map(s => ({ ...s, tipo: 'outro' as const }))
  ] : [];

  const filteredSocios = filterSocios(allSocios as any);

  return (
    <Card
      title={
        <Space>
          <TeamOutlined />
          <span>Buying Committee</span>
        </Space>
      }
      extra={
        <Space>
          <Tag color="gold">{contagem.decisores} Decisor</Tag>
          <Tag color="blue">{contagem.influenciadores}</Tag>
          <Tag>{contagem.outros}</Tag>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {error && (
        <Empty description={error} />
      )}

      {!loading && committee && (
        <>
          <Input
            placeholder="Buscar contato..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ marginBottom: 12 }}
            size="small"
          />

          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <List
              size="small"
              dataSource={filteredSocios}
              renderItem={(socio: any) => renderSocioItem(socio, socio.tipo)}
              locale={{ emptyText: 'Nenhum sócio encontrado' }}
            />
          </div>

          {emails.length > 0 && (
            <>
              <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <MailOutlined /> Emails Inferidos ({emails.length})
                </Text>
                <List
                  size="small"
                  dataSource={emails}
                  renderItem={(email) => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <Space>
                        <Text style={{ fontSize: 12 }}>{email.nome}:</Text>
                        <Text code style={{ fontSize: 11 }}>{email.email_inferido}</Text>
                        <Tooltip title="Copiar">
                          <Button 
                            type="text" 
                            size="small" 
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(email.email_inferido)}
                          />
                        </Tooltip>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            </>
          )}
        </>
      )}

      {!loading && !committee && !error && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Button onClick={fetchCommittee} icon={<ReloadOutlined />}>
            Carregar Sócios
          </Button>
        </div>
      )}
    </Card>
  );
};

export default BuyingCommitteeCard;
